// CR-7732: the Haulier picker for CallOffWizard. Its own hook so
// useCallOffWizardState stays under the 500-line cap.
import { useState, useEffect, useMemo } from 'react';
import { callOffAPI } from '../../../lib/api';
import type { CallOff, HaulierOption } from '../../../lib/api';
import { toast } from 'sonner';

/**
 * Loads the customer's hauliers when the wizard opens and holds the chosen one.
 *
 * In edit mode the current haulier is pre-selected. If it has since dropped
 * off the customer's carrier list it is still shown, so the picker never
 * silently reads "No haulier" for a call-off that has one.
 *
 * @param open - whether the wizard is open; options are fetched on open
 * @param editCallOff - the call-off being edited, or null/undefined in create mode
 * @returns the options, loading flag, the selected haulier id and its setter,
 *   and whether the haulier is locked (the load is in transit or later)
 *
 * @example
 * const { haulierOptions, haulierId, setHaulierId } = useCallOffWizardHaulier(open, editCallOff);
 */
export function useCallOffWizardHaulier(open: boolean, editCallOff?: CallOff | null) {
  const [options, setOptions] = useState<HaulierOption[]>([]);
  const [loadingHauliers, setLoadingHauliers] = useState(false);
  const [haulierId, setHaulierId] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingHauliers(true);
    callOffAPI.getHaulierOptions()
      .then(res => { if (!cancelled) setOptions(res.data || []); })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error('Failed to load hauliers:', err);
        toast.error('Failed to load hauliers');
      })
      .finally(() => { if (!cancelled) setLoadingHauliers(false); });
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setHaulierId(editCallOff?.haulier ?? null);
  }, [open, editCallOff]);

  const haulierOptions = useMemo(
    () => (editCallOff ? includeCurrentHaulier(options, editCallOff) : options),
    [options, editCallOff],
  );

  const haulierLocked = !!editCallOff && editCallOff.can_change_haulier === false;

  return { haulierOptions, loadingHauliers, haulierId, setHaulierId, haulierLocked };
}

/**
 * The haulier options with the call-off's current haulier kept in the list.
 *
 * A haulier whose link was switched off is no longer offered, but a call-off
 * already assigned to it must still show it rather than read "No haulier".
 *
 * @param options - the customer's current hauliers
 * @param callOff - the call-off whose haulier must stay selectable
 * @returns options, with the current haulier prepended when it is missing
 */
export function includeCurrentHaulier(options: HaulierOption[], callOff: CallOff): HaulierOption[] {
  const current = callOff.haulier;
  if (!current || options.some(o => o.id === current)) return options;
  return [{ id: current, name: callOff.haulier_name || 'Current haulier', company_type: '' }, ...options];
}
